/**
 * useSimulatorAudio — MediaRecorder + Whisper transcription.
 *
 * UX: hold-to-record (or click-to-toggle), stop produces blob, blob is sent to
 * `/simulator/transcribe`, transcript is returned to the caller (typically
 * filling the chat textarea so the user can review/edit before sending).
 *
 * The audio blob is NOT auto-uploaded as a message attachment; the caller
 * decides what to do with it. F5 ships text-only handoff (transcript only).
 *
 * Tier 3 (Mb6 + Mb3):
 *   - Acquires a Screen Wake Lock during capture so iOS Safari does not
 *     suspend the page when the screen dims (loses the in-memory chunks).
 *   - Listens for `visibilitychange`; warns the user if they background
 *     the tab and surfaces a recovery error if the recorder ended up in
 *     `inactive` state on return (browser killed it).
 *   - Registers itself in the global `useRecorderState` store so chrome
 *     (e.g. FloatingFeedbackButton) can hide while a session is active.
 */
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { transcribeAudio } from "../api/client";
import { toast } from "@/lib/toast";
import { toast as sonnerToast } from "sonner";
import {
  isNoConfiguredProviderError,
  showNoConfiguredProviderToast,
} from "@/lib/edgeFnError";
import { useRecorderState } from "@/features/recorder/state/recorderState";

export type AudioPhase = "idle" | "recording" | "transcribing";

export interface UseSimulatorAudioReturn {
  phase: AudioPhase;
  isSupported: boolean;
  start: () => Promise<void>;
  stopAndTranscribe: () => Promise<string | null>;
  cancel: () => void;
}

const MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/ogg;codecs=opus",
  "audio/mp4",
];

function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined;
  for (const m of MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return undefined;
}

interface WakeLockSentinelLike {
  release: () => Promise<void>;
}

async function requestWakeLock(): Promise<WakeLockSentinelLike | null> {
  try {
    const nav = navigator as Navigator & {
      wakeLock?: { request: (type: "screen") => Promise<WakeLockSentinelLike> };
    };
    if (!nav.wakeLock?.request) return null;
    return await nav.wakeLock.request("screen");
  } catch {
    return null;
  }
}

export function useSimulatorAudio(): UseSimulatorAudioReturn {
  const { session } = useSession();
  const [phase, setPhase] = useState<AudioPhase>("idle");
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const mimeRef = useRef<string | undefined>(undefined);
  const wakeLockRef = useRef<WakeLockSentinelLike | null>(null);
  const recorderId = useId();
  const setRecording = useRecorderState((s) => s.setRecording);

  const isSupported =
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== "undefined";

  const releaseWakeLock = useCallback(async () => {
    const sentinel = wakeLockRef.current;
    if (!sentinel) return;
    wakeLockRef.current = null;
    try {
      await sentinel.release();
    } catch {
      // ignore
    }
  }, []);

  const cleanup = useCallback(() => {
    recorderRef.current = null;
    chunksRef.current = [];
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setRecording(recorderId, false);
    void releaseWakeLock();
  }, [recorderId, releaseWakeLock, setRecording]);

  const start = useCallback(async () => {
    if (!isSupported) {
      toast.error("Seu navegador não suporta gravação de áudio.");
      return;
    }
    if (phase !== "idle") return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMimeType();
      mimeRef.current = mime;
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorderRef.current = rec;
      rec.start();
      setPhase("recording");
      setRecording(recorderId, true);
      void requestWakeLock().then((sentinel) => {
        if (sentinel) wakeLockRef.current = sentinel;
      });
    } catch (err) {
      cleanup();
      // A17: map browser errors to platform-specific recovery instructions.
      // Default browser error messages are cryptic ("Permission denied"); users
      // need to know WHERE to re-enable the mic.
      const name = err instanceof Error ? err.name : "";
      if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        toast.error(
          "Microfone bloqueado. Clique no cadeado ao lado do endereço (Chrome/Edge) ou em Ajustes do site (Safari/iOS) e libere o microfone para este site, depois recarregue a página.",
        );
      } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        toast.error("Nenhum microfone detectado. Conecte um microfone e tente novamente.");
      } else if (name === "NotReadableError" || name === "TrackStartError") {
        toast.error("Microfone está sendo usado por outro app. Feche outras chamadas/gravações e tente novamente.");
      } else {
        const reason = err instanceof Error ? err.message : "Erro ao acessar microfone";
        toast.error(`Erro ao iniciar gravação: ${reason}`);
      }
    }
  }, [cleanup, isSupported, phase, recorderId, setRecording]);

  const stopAndTranscribe = useCallback(async (): Promise<string | null> => {
    const rec = recorderRef.current;
    if (!rec || phase !== "recording") return null;

    const blob: Blob = await new Promise((resolve) => {
      rec.onstop = () => {
        const type = mimeRef.current ?? "audio/webm";
        resolve(new Blob(chunksRef.current, { type }));
      };
      rec.stop();
    });
    cleanup();
    setPhase("transcribing");

    if (blob.size < 500) {
      setPhase("idle");
      toast.error("Áudio muito curto. Tente novamente.");
      return null;
    }

    try {
      const token = await session?.getToken();
      if (!token) {
        setPhase("idle");
        toast.error("Sessão expirou. Faça login de novo.");
        return null;
      }
      const ext = (mimeRef.current ?? "audio/webm").split(";")[0].split("/")[1] ?? "webm";
      const filename = `simulator-audio-${Date.now()}.${ext}`;
      const res = await transcribeAudio(blob, filename, token);
      setPhase("idle");
      return res.transcript;
    } catch (err) {
      setPhase("idle");
      const e = err as Error & { code?: string; status?: number };
      if (
        isNoConfiguredProviderError(err) ||
        e.code === "no_transcription_key" ||
        e.status === 402
      ) {
        showNoConfiguredProviderToast();
        return null;
      }
      if (e.code === "transcription_degenerate") {
        toast.error("Áudio sem fala detectável. Tente falar mais claro e novamente.");
      } else if (e.status === 429) {
        toast.error("Limite de transcrições por hora atingido.");
      } else {
        toast.error(e.message ?? "Erro ao transcrever áudio.");
      }
      return null;
    }
  }, [cleanup, phase, session]);

  const cancel = useCallback(() => {
    if (recorderRef.current && phase === "recording") {
      try {
        recorderRef.current.stop();
      } catch {
        // ignore
      }
    }
    cleanup();
    setPhase("idle");
  }, [cleanup, phase]);

  // Mb6: warn on tab background, detect kill on return, re-acquire wake lock.
  useEffect(() => {
    if (phase !== "recording") return;
    const handler = () => {
      if (document.hidden) {
        sonnerToast.warning(
          "Mantenha a tela ativa para não interromper a gravação.",
          { duration: 6000 },
        );
      } else {
        const rec = recorderRef.current;
        if (rec && rec.state === "inactive") {
          // Browser killed the recorder while hidden. Surface recovery hint.
          toast.error(
            "Gravação interrompida em background. Tente gravar novamente — partes podem ter sido perdidas.",
          );
          cleanup();
          setPhase("idle");
          return;
        }
        if (!wakeLockRef.current) {
          void requestWakeLock().then((sentinel) => {
            if (sentinel) wakeLockRef.current = sentinel;
          });
        }
      }
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, [phase, cleanup]);

  // Final unmount safety: drop wake lock + global flag.
  useEffect(() => {
    return () => {
      setRecording(recorderId, false);
      void releaseWakeLock();
    };
  }, [recorderId, releaseWakeLock, setRecording]);

  return { phase, isSupported, start, stopAndTranscribe, cancel };
}
